import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import {
  defaultImagePlaceholder,
  genThumbMediaURI,
  isMediaInfoPublishable,
  PropertyMediaInfo,
  SecureImage,
} from '@miq/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DynamicLocaleService } from './dynamic-locale.service';
import { DataObject, ReportOptions, ReportRecord } from './models/report.model';
import {
  LovValues,
  ReportAllRecordsImage,
  ReportRecordImage,
  TemplateField,
  TemplateFieldsDisplayFn,
  ViewTemplateCatalog,
  VisibilitySettings,
} from './models/template.model';
import { ReportExportViewService } from './report-export-view.service';
import { REPORT_EXPORT_CONFIGS_TOKEN, ReportExportConfigurations } from './report-export-view.token';
import { ReportImagesService } from './report-images.service';
import {
  ADDITIONAL_IMAGE_FIELD_ID,
  AdditionalImagesPosition,
  AVAILABILITY_FLOOR_PLAN,
  FLOORPLAN_FIELD_ID,
  getFloorPlanImage,
  getSecureImage,
  IMAGE_FIELD_ID,
  IMAGE_STATIC,
  LAYOUT_IMAGE_3_ID,
} from './shared/templates/config/common';
import { TEMPLATE_CONFIGURATIONS } from './shared/templates/template-display-fn.const';
import { TemplateLovAttributesReqPayload } from './shared/templates/template-lov-attributes.const';

@UntilDestroy()
@Injectable()
export class ReportFieldFormatterService implements OnDestroy {
  public reportAllImages: ReportAllRecordsImage;
  constructor(
    @Inject(REPORT_EXPORT_CONFIGS_TOKEN) private reportModuleConfigs: ReportExportConfigurations,
    reportImagesService: ReportImagesService,
    private dynamicLocaleService: DynamicLocaleService,
    private reportExportViewService: ReportExportViewService,
    private httpClient: HttpClient,
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger
  ) {
    // TODO: analyze to remove this code since seems not being used by anyone outside this lib.
    reportImagesService.onUpdateAdditionalImage$.pipe(untilDestroyed(this)).subscribe(response => {
      this.reportAllImages = response;
    });
  }

  ngOnDestroy() {
    // REQUIRED BY UNTIL DESTROY
  }

  getAndFormatField(
    record: ReportRecord | ReportRecord[],
    settings: {
      templateId: number;
      field: TemplateField;
      joinString: boolean;
      onlyUrl?: boolean;
      isPdf?: boolean;
      symbols?: DataObject;
      reportId?: number;
      reportOptions?: ReportOptions;
      recordsLength?: number;
      templateFieldsCatalog?: ViewTemplateCatalog;
    }
  ): Observable<string | string[] | SecureImage> {
    if (!TEMPLATE_CONFIGURATIONS[settings.templateId]) {
      return;
    }
    let result: Observable<string | string[] | SecureImage>;
    const { fieldsDisplayFunctions } = TEMPLATE_CONFIGURATIONS[settings.templateId];
    if (!Array.isArray(record)) {
      if (settings.field != null && settings.field.id === IMAGE_STATIC) {
        // Do nothing
      } else if (settings.field != null && settings.field.id === IMAGE_FIELD_ID) {
        result = this.getSecureImage(record, settings);
      } else if (settings.field != null && settings.field.id === ADDITIONAL_IMAGE_FIELD_ID) {
        result = this.getAdditionalImage(record, fieldsDisplayFunctions, settings);
      } else if (settings.field != null && settings.field.id === LAYOUT_IMAGE_3_ID) {
        result = this.getSecondaryImage(record, fieldsDisplayFunctions, settings);
      } else if (settings.field != null && settings.field.id === AVAILABILITY_FLOOR_PLAN) {
        result = this.getAvailabilityImage(record, fieldsDisplayFunctions, settings);
      } else if (settings.field != null && FLOORPLAN_FIELD_ID.includes(settings.field.id)) {
        result = this.getFloorPlanImage(record, settings);
      } else if (settings.field != null) {
        result = this.getRegularField(record, fieldsDisplayFunctions, settings);
      }
    } else {
      result = this.getRegularField(record, fieldsDisplayFunctions, settings);
    }
    return settings.onlyUrl ? (result as Observable<string | string[]>) : result;
  }

  private getFloorPlanImage(
    record,
    settings: { onlyUrl?: boolean; isPDF?: boolean }
  ): Observable<string | SecureImage> {
    const { onlyUrl = false, isPDF = false } = settings;
    const { edpMultimediaURI, s3BucketImagesUrl } = this.reportModuleConfigs;
    return of(
      getFloorPlanImage(record, { urlPrefix: s3BucketImagesUrl, urlSplitter: edpMultimediaURI, onlyUrl, isPDF })
    );
  }

  private getSecureImage(
    record,
    settings: { onlyUrl?: boolean; isPDF?: boolean; field?: TemplateField }
  ): Observable<string | SecureImage> {
    const { isPDF = false, field } = settings;
    let onlyUrl = settings.onlyUrl || false;
    const { edpMultimediaURI, s3BucketImagesUrl } = this.reportModuleConfigs;
    if (field && field.isPublishedPrimaryImagesOnly) {
      onlyUrl = false;
    }
    const result = getSecureImage(record, {
      urlPrefix: s3BucketImagesUrl,
      urlSplitter: edpMultimediaURI,
      onlyUrl,
      isPDF,
    });
    // when isPublishedPrimaryImagesOnly is true, then we show primary image only if it is published, else we show placeholder image
    if (field && field.isPublishedPrimaryImagesOnly && result) {
      if (!(result as SecureImage).publishable) {
        result['imageSrc'] = defaultImagePlaceholder;
      }
      return of(settings.onlyUrl ? result['imageSrc'] : result);
    }
    return of(result);
  }

  private getRegularField(
    record: ReportRecord | ReportRecord[],
    templateDisplayFnCatalog: TemplateFieldsDisplayFn,
    settings: {
      templateId?: number;
      joinString?: boolean;
      field?: TemplateField;
      symbols?: DataObject;
      reportId?: number;
      reportOptions?: ReportOptions;
      recordsLength?: number;
      templateFieldsCatalog?: ViewTemplateCatalog;
    }
  ): Observable<string | string[]> {
    const { templateFieldsCatalog, templateId, joinString = true, symbols, reportId, reportOptions } = settings;
    let result: Observable<string | string[]> = of('-');
    const { recordsLength, field } = settings;
    const { id, isPPTHasSpecificFormat, displayFnArgumentsOrder } = field;
    const { edpMultimediaURI, s3BucketImagesUrl } = this.reportModuleConfigs;

    const options = {
      locale: this.dynamicLocaleService.getReportLocaleId(),
      jsonData: this.dynamicLocaleService.fetchJsonData(),
      httpClient: this.httpClient,
      reportExportViewService: this.reportExportViewService,
      displayFnArgumentsOrder,
      symbols,
      isPPTHasSpecificFormat,
      templateFieldsCatalog,
      templateId,
      reportId,
      reportOptions,
      recordsLength,
      urlPrefix: s3BucketImagesUrl,
      urlSplitter: edpMultimediaURI,
    };
    const displayFnConfiguration = templateDisplayFnCatalog[id];
    try {
      /*
       * NOTE(edmundo): this try and catch is here to prevent blueprint download
       * to fail. This will catch any error with the display function
       * except for the observables. Observables (i.e. errors within the map operator)
       * must be caught in a different place.
       */
      result =
        displayFnConfiguration != null && displayFnConfiguration.displayFn != null
          ? (displayFnConfiguration.displayFn(record, options, ...settings.field.displayFnArguments) as Observable<
              string | string[]
            >)
          : result;
    } catch (e) {
      this.logger.error(`field ID: ${id}: ${e}`);
      result = of('-');
    }
    const observableResult = result instanceof Observable ? result : of(result);
    return observableResult.pipe(
      map((res: string | string[]) => {
        const constructedResult = this.constructResult(res, joinString);
        return constructedResult;
      })
    );
  }

  private constructResult(result: string | string[], joinString: boolean) {
    return joinString && Array.isArray(result) ? result.join(' ') || '-' : result || '-';
  }

  /*
  To get Formatted additional image by passing all extra images of a report for every record
  */
  private getAdditionalImage(
    record: ReportRecord,
    templateDisplayFnCatalog: TemplateFieldsDisplayFn,
    settings: { onlyUrl?: boolean; field: TemplateField }
  ): Observable<string | SecureImage> {
    const { onlyUrl = false, field } = settings;
    let recordEntityId = record.associatedProperty ? record.associatedProperty.entity.id : record.data.entity.id;
    if (field.pickFromEntity && !Array.isArray(record.data)) {
      recordEntityId = -record.data.entity.id;
    }

    const { edpMultimediaURI, s3BucketImagesUrl } = this.reportModuleConfigs;

    const displayFnConfiguration = templateDisplayFnCatalog[field.id];
    const mediaInfo =
      displayFnConfiguration != null && displayFnConfiguration.displayFn != null
        ? (displayFnConfiguration.displayFn(record, null) as PropertyMediaInfo[])
        : [];

    let imageInfo: ReportRecordImage = null;
    if (!!this.reportAllImages[recordEntityId]) {
      imageInfo = this.reportAllImages[recordEntityId][AdditionalImagesPosition.AdditionalImage];
    }
    if (!!imageInfo) {
      return this.getImage(record, mediaInfo, settings, AdditionalImagesPosition.AdditionalImage);
    } else if (!field.pickFromEntity) {
      const mediaData = (mediaInfo || []).filter(media => !media.primary_image_f);
      if (mediaData && mediaData.length) {
        const sortedMedia = ((Array.isArray(mediaData) && mediaData) || []).sort(
          (imageA, imageB) => imageA.display_order - imageB.display_order
        );
        const result: SecureImage = {
          imageSrc: genThumbMediaURI(
            edpMultimediaURI,
            s3BucketImagesUrl,
            sortedMedia ? sortedMedia[0].media_path : null,
            { provideDefault: true }
          ),
          publishable: isMediaInfoPublishable(sortedMedia[0]),
        };
        const imagePath = onlyUrl ? (result.imageSrc as string) : result;
        return of(imagePath);
      }
      return of(
        onlyUrl
          ? 'assets/svg/building_placeholder.svg'
          : { imageSrc: 'assets/svg/building_placeholder.svg', publishable: true }
      );
    }
  }
  /*
    To get Formatted third image by passing all extra images of a report for every record
  */
  private getSecondaryImage(
    record: ReportRecord,
    templateDisplayFnCatalog: TemplateFieldsDisplayFn,
    settings: { onlyUrl?: boolean; field: TemplateField }
  ): Observable<string | SecureImage> {
    const displayFnConfiguration = templateDisplayFnCatalog[settings.field.id];
    const mediaInfo =
      displayFnConfiguration != null && displayFnConfiguration.displayFn != null
        ? (displayFnConfiguration.displayFn(record, null) as PropertyMediaInfo[])
        : [];
    return this.getImage(record, mediaInfo, settings, AdditionalImagesPosition.SecondaryImage);
  }

  private getAvailabilityImage(
    record: ReportRecord,
    templateDisplayFnCatalog: TemplateFieldsDisplayFn,
    settings: { onlyUrl?: boolean; field: TemplateField }
  ): Observable<string | SecureImage> {
    const displayFnConfiguration = templateDisplayFnCatalog[settings.field.id];
    const mediaInfo =
      displayFnConfiguration != null && displayFnConfiguration.displayFn != null
        ? (displayFnConfiguration.displayFn(record, null) as PropertyMediaInfo[])
        : [];
    let idToUse = -1;
    if (!Array.isArray(record.data) && record.data.entity != null) {
      idToUse = record.data.entity.id;
    } else if (record.edpRecordId != null) {
      idToUse = record.edpRecordId;
    }
    const reportAllImages = this.reportAllImages || {};
    const locatedImageInfo: ReportRecordImage =
      this.reportAllImages[idToUse] && mediaInfo != null
        ? reportAllImages[idToUse].find(a => mediaInfo.some(b => b.id === a.imageId))
        : null;
    const newSettings = { ...settings, locatedImageInfo };
    return this.getImage(record, mediaInfo, newSettings);
  }

  private getImage(
    record: { [key: string]: any },
    mediaInfo: PropertyMediaInfo[],
    settings: { onlyUrl?: boolean; field: TemplateField; locatedImageInfo?: ReportRecordImage },
    imagePosition?: number
  ): Observable<string | SecureImage> {
    const { edpMultimediaURI, s3BucketImagesUrl } = this.reportModuleConfigs;
    const { locatedImageInfo, field } = settings;
    let recordEntityId = record.associatedProperty ? record.associatedProperty.entity.id : record.data.entity.id;
    if (field.pickFromEntity && !Array.isArray(record.data)) {
      recordEntityId = -record.data.entity.id;
    }
    let imageInfo: ReportRecordImage;
    if (locatedImageInfo != null) {
      imageInfo = locatedImageInfo;
    } else if (imagePosition != null) {
      imageInfo =
        this.reportAllImages && this.reportAllImages[recordEntityId]
          ? this.reportAllImages[recordEntityId][imagePosition]
          : null;
    }
    if (!imageInfo) {
      return null;
    }
    const imageFound: PropertyMediaInfo = (mediaInfo || []).find(image => image.id === imageInfo.imageId);
    const result: SecureImage = {
      imageSrc: genThumbMediaURI(
        edpMultimediaURI,
        s3BucketImagesUrl,
        imageFound != null ? imageFound.media_path : null,
        { provideDefault: true }
      ),
      publishable: isMediaInfoPublishable(imageFound),
    };
    const imagePath = settings.onlyUrl ? (result.imageSrc as string) : result;
    return of(imagePath);
  }

  public getLovValues(lookup: string, columnID: number): Observable<LovValues[]> | Observable<{}> {
    const localeId: number = this.dynamicLocaleService.getReportLocaleId();
    const reqPayLoad: any[] = TemplateLovAttributesReqPayload[columnID];
    let result: Observable<LovValues[]> | Observable<{}>;
    result = this.reportExportViewService.getLovObservablesData(lookup, reqPayLoad, localeId);
    return result;
  }

  public isFieldVisible(record: ReportRecord | ReportRecord[], settings: VisibilitySettings) {
    const { fieldsDisplayFunctions } = TEMPLATE_CONFIGURATIONS[settings.templateId];
    const displayFnConfiguration = fieldsDisplayFunctions[settings.field.id];
    return displayFnConfiguration != null && displayFnConfiguration.isVisible != null
      ? displayFnConfiguration.isVisible(record as ReportRecord, settings)
      : true;
  }

  public getLabelDesc(
    record: ReportRecord | ReportRecord[],
    settings: {
      templateId: number;
      field: TemplateField;
    }
  ): string {
    const { fieldsDisplayFunctions } = TEMPLATE_CONFIGURATIONS[settings.templateId];
    const displayFnConfiguration = fieldsDisplayFunctions[settings.field.id];
    const result =
      displayFnConfiguration != null && displayFnConfiguration.labelDisplayFn != null
        ? displayFnConfiguration.labelDisplayFn(record, null, ...settings.field.displayFnArguments)
        : settings.field.label;
    return result as string;
  }
}
